import React from 'react';
import { useTranslation } from 'react-i18next';
import { AttributePill, SystemChip } from './pills';
import { rowActivation } from './rowActivation';

/**
 * TicketCardRow (phone) + TicketGridRow (desktop table) — #62 next-phase Tickets depth spec (UXeng
 * §2). Comp columns: Ticket ID · Subject · Status · Priority · Assignee. Phone card: title Subject,
 * meta Priority, Priority is NOT a pill on phone (2 of the 3-pill budget spent), matching the comp.
 *
 * A4 (D-K1) — the shape now tells the truth:
 *  - STATUS is a SystemChip: square, no chevron, NOT a button. Ticket status is action-driven
 *    (resolve/reopen in the detail), so a round chevroned pill advertised "pick a value" on a field
 *    where picking is not the verb — and, worse, its unconditional stopPropagation swallowed the
 *    row-open tap: a dead zone on the most obviously tappable thing on the row.
 *  - ASSIGNEE is a WIRED AttributePill (DN-7 ruling: wire, don't demote — the ValueSheet already
 *    exists for this field in the detail panel). The pill's promise now has an editor behind it.
 */
export interface TicketRow {
  id: string;
  number: string;
  subject: string;
  priority: string | null;
  status: string;
  assigneeId: string | null;
  assigneeName: string | null;
}

export interface TicketRowCallbacks {
  selected?: boolean;
  onOpenTicket: (ticket: TicketRow) => void;
  onEditAssignee: (ticket: TicketRow, anchor: HTMLElement, shape: 'sheet' | 'popover') => void;
}

export const TicketCardRow: React.FC<{ ticket: TicketRow } & TicketRowCallbacks> = ({ ticket, selected, onOpenTicket, onEditAssignee }) => {
  const { t } = useTranslation('common');
  return (
    <div {...rowActivation(() => onOpenTicket(ticket))} data-testid="ticket-row" onClick={() => onOpenTicket(ticket)}
      className={`p-3 rounded-xl bg-surface border ${selected ? 'ring-2 ring-primary/15 border-primary/30' : 'border-border-neutral'} cursor-pointer`}>
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-text-strong truncate">{ticket.subject || '—'}</span>
      </div>
      {ticket.priority && <div className="text-xs text-text-muted">{t(`ticketsGrid.priority.${ticket.priority}`)}</div>}
      <div className="flex flex-wrap items-center gap-2 mt-1.5">
        <SystemChip label={t(`ticketsGrid.status.${ticket.status}`)} title={t('ticketsGrid.columns.status')} testId="ticket-status-chip" />
        <AttributePill value={ticket.assigneeName} label={t('ticketsGrid.columns.assignee')}
          emptyText={t('customers.detail.setField', { field: t('ticketsGrid.columns.assignee') })}
          onOpen={(a) => onEditAssignee(ticket, a, 'sheet')} />
      </div>
    </div>
  );
};

export const TicketGridRow: React.FC<{ ticket: TicketRow } & TicketRowCallbacks> = ({ ticket, selected, onOpenTicket, onEditAssignee }) => {
  const { t } = useTranslation('common');
  return (
    <tr data-testid="ticket-row" tabIndex={0} onKeyDown={rowActivation(() => onOpenTicket(ticket)).onKeyDown}
      className={`border-b border-border-neutral hover:bg-canvas cursor-pointer ${selected ? 'bg-canvas' : ''}`} onClick={() => onOpenTicket(ticket)}>
      <td className="sticky z-10 bg-surface p-cell-y px-cell-x [inset-inline-start:0]">
        <span className="text-sm font-semibold text-text-strong truncate">{ticket.number}</span>
      </td>
      <td className="p-cell-y px-cell-x text-sm text-text-strong truncate max-w-xs">{ticket.subject || '—'}</td>
      <td className="p-cell-y px-cell-x whitespace-nowrap">
        <SystemChip label={t(`ticketsGrid.status.${ticket.status}`)} title={t('ticketsGrid.columns.status')} testId="ticket-status-chip" />
      </td>
      <td className="p-cell-y px-cell-x text-sm text-text-muted whitespace-nowrap">{ticket.priority ? t(`ticketsGrid.priority.${ticket.priority}`) : '—'}</td>
      <td className="p-cell-y px-cell-x whitespace-nowrap">
        <AttributePill value={ticket.assigneeName} label={t('ticketsGrid.columns.assignee')}
          emptyText={t('customers.detail.setField', { field: t('ticketsGrid.columns.assignee') })}
          onOpen={(a) => onEditAssignee(ticket, a, 'popover')} />
      </td>
    </tr>
  );
};
